import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import type { TextPassageSize } from './text-passage.model';
import styles from './text-passage.styles';

/**
 * @slot default - The text passage content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksTextPassage extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Inverted variant
   * 1) Used for dark backgrounds
   */
  @property({ type: Boolean })
  inverted: boolean | undefined;

  /**
   * Size variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **xs** renders smaller typography than the `sm` variant</li>
   * <li> **sm** renders smaller typography than the default</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: TextPassageSize;

  /**
   * Add the light dom styles when this component is connected to a page
   */
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * First updated lifecycle hook
   * 1) Add class names to the wrapper cat-text-passage to allow the shadow DOM
   * to manipulate in React wrappers. Attributes get turned into props so we need
   * to append these class names to get the variants to style properly
   * IMPORTANT: variants are defined here so light DOM can manipulate the styles of
   * nested elements
   */
  firstUpdated() {
    if (this.size === 'sm') {
      this.classList.add('cat-text-passage--sm'); /* 1 */
    }
    if (this.size === 'xs') {
      this.classList.add('cat-text-passage--xs'); /* 1 */
    }

    if (this.inverted) {
      this.classList.add('cat-text-passage--inverted'); /* 1 */
    }
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-text-passage', {
      'cat-text-passage--sm': this.size === 'sm',
      'cat-text-passage--xs': this.size === 'xs',
      'cat-text-passage--inverted': this.inverted
    });

    return html`
      <div part="base" class="${componentClassName}">
        <slot part="part-default"></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-text-passage') === undefined) {
  customElements.define('cat-text-passage', BlocksTextPassage);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-text-passage': BlocksTextPassage;
  }
}
